import { useEffect, useState, useRef } from 'react';
import OptionCard from 'src/components/OptionCard';
import { ApiRoutes } from 'src/lib/routes';
import { Option, Pagination as TPagination, FilterableType } from 'src/types';
import useQuery from 'src/hooks/useQuery';
import { generateArrayDates, getEleOffset, scrollTo } from 'src/helpers';
import useSearchBarState from 'src/hooks/useSearchBarState';
import { min, range } from 'lodash-es';
import Loading from './Loading';
import OptionCardSkeleton from 'src/components/OptionCardSkeleton';
import { useTranslations } from 'next-intl';
import Pagination from './Pagination';
import { useRouter } from 'next/router';
import useUiSpecs from 'src/hooks/useUiSpecs';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import ApiClient from 'src/lib/api_client';
import OptionCardEdit from './OptionCardEdit';

type Props = {
  hideTitle?: boolean;
  renderTitle?: () => JSX.Element;
  isGiftExperience?: boolean;
  usePagination?: boolean;
  scrollLoadMore?: boolean;
  noQuery?: boolean;
  fallbackData?: Option[];
  afterFetch?: (count: number) => void;
  record: {
    id?: string | number;
    type: FilterableType;
  };
  queryParams?: {
    [key: string]: string | number | string[] | number[] | null | (number | string | undefined)[];
  };
  hidePrice?: boolean;
  shouldScrollTop?: boolean;
  useHtmlLink?: boolean;
  show?: boolean;
  initialTotalCount?: number;
  isEditing?: boolean
  onCancel?: ()=> void;
  onSave?: ()=> void;
};

type ListOption = { data: Option[]; pagination: TPagination };
type OptionEditFields = { name?: string; subtitle?: string };

const OptionEditActions: React.FC<{
  options: Option[];
  onCancel: () => void;
  onSave: () => void;
  onGenerated: (data: OptionEditFields) => void;
  pendingOptionChangesById: Record<number, OptionEditFields>;
}> = ({ options, pendingOptionChangesById, onGenerated, onCancel, onSave }) => {
  const translation = useTranslations('components.OtherOptions');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const generateOptionSubtitles = useEnhanceApiClient(ApiClient.generateOptionSubtitles);
  const updateOptionNameSubtitle = useEnhanceApiClient(ApiClient.updateOptionNameSubtitle);

  return (
    <div className="tw-mb-8">
      {error ? <div className="tw-text-red tw-text-sm">{error}</div> : null}
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-text-orange tw-underline tw-cursor-pointer tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none">
          {isGenerating ? (
            <div>
              <div className="spinner-border spinner-border-sm tw-text-orange tw-mr-2"></div>
              generating...
            </div>
          ) : (
            <div
              onClick={() => {
                setIsGenerating(true);
                generateOptionSubtitles({
                  optionIds: options.map((o) => o.id),
                })
                  .then((result) => {
                    const [json] = result;
                    if ('error' in json && json.error) {
                      setError(json.error);
                    } else {
                      setError('');
                      const subtitles = json as OptionEditFields;
                      typeof onGenerated === 'function' && onGenerated(subtitles);
                    }
                  })
                  .catch((e) => {
                    if (e instanceof Error) {
                      setError(e.message);
                    }
                  })
                  .finally(() => {
                    setIsGenerating(false);
                  });
              }}
            >
              {translation('generateAllExcerpts')}
            </div>
          )}
        </div>
        <div className="tw-flex tw-items-center">
          <div
            className="tw-mr-2 tw-font-styleSans tw-font-semibold tw-cursor-pointer"
            onClick={() => {
              if (isSaveLoading) return;
              setIsSaveLoading(false);
              setIsGenerating(false);
              typeof onCancel === 'function' && onCancel();
            }}
          >
            {translation('cancel')}
          </div>
          <div
            className="btn btn-primary tw-ml-2"
            onClick={() => {
              setIsSaveLoading(true);
              const bulkItems = Object.entries(pendingOptionChangesById).map(
                ([optionId, attrs]) => {
                  return { id: optionId, ...attrs };
                }
              );

              updateOptionNameSubtitle({ items: bulkItems })
                .then((result) => {
                  const [json] = result;
                  if ('error' in json && json.error) {
                    setError(json.error);
                  } else {
                    setIsSaveLoading(false);
                    typeof onSave === 'function' && onSave();
                  }
                })
                .catch((e) => {
                  if (e instanceof Error) {
                    setError(e.message);
                  }
                })
                .finally(() => {
                  setIsSaveLoading(false);
                });
            }}
          >
            {isSaveLoading ? (
              <div className="spinner-border spinner-border-sm tw-text-white tw-mr-2"></div>
            ) : null}
            {translation('save')}
          </div>
        </div>
      </div>
    </div>
  );
};

const OtherOptions: React.FC<Props> = ({
  hideTitle,
  renderTitle,
  isGiftExperience,
  usePagination,
  scrollLoadMore,
  noQuery,
  fallbackData,
  record,
  queryParams,
  afterFetch,
  hidePrice,
  shouldScrollTop,
  useHtmlLink,
  initialTotalCount,
  isEditing,
  onCancel,
  onSave,
  show = true
}) => {
  const { headerHeight, pageNavHeight } = useUiSpecs()
  const router = useRouter()
  const { page: qPage } = router.query
  const translation = useTranslations('components.OtherOptions')
  const [searchParams] = useSearchBarState();
  const scrollLoadMoreRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState<number | undefined>(undefined);
  const [pendingOptionChangesById, setPendingOptionChangesById] = useState<Record<number, OptionEditFields>>({})
  const fallback: ListOption = { data: fallbackData || [], pagination: {} as TPagination };
  const reqParams = {
    per_page: 12,
    record_id: record.id,
    record_type: record.type,
    page: page,
    start_date:
      searchParams.dates.length === 2
        ? generateArrayDates(searchParams.dates[0], searchParams.dates[1])
        : searchParams.dates,
    qty: searchParams.numGuest,
    ...(show ? queryParams || { sort_by: 'recommended' } : {}),
  };
  const numSkeletons = min([initialTotalCount || 0, 12]) as number
  const { data, isLoading, mutate } = useQuery<ListOption>(
    noQuery
      ? null
      : (record.id && record.type || record.type === 'Fts') && page && page > 0
      ? ApiRoutes.apiFilterRoute({}, reqParams).toUrl()
      : null,
    {
      fetcherOpts: {
        corsEnable: true,
      }
    }
  );
  const [options, setOptions] = useState<Option[]>(fallback.data);

  const { data: fetchedOptions, pagination } = data || { ...fallback, data: options };

  useEffect(() => {
    if (page && page > 1 && !usePagination) {

      setOptions((prev) => {
        const newOptions = fetchedOptions.filter(o => !prev.find(v => v.id === o.id))
        return newOptions.length > 0 ? [...prev, ...newOptions] : prev
      });
    } else {
      setOptions(fetchedOptions);
      const timer = setTimeout(() => {
        if (shouldScrollTop && !usePagination) {
          window.scrollTo(0, 0);
        }
      }, 200);

      return () => {
        window.clearTimeout(timer);
      };
    }
  }, [fetchedOptions.map((o) => o.id).join(','), page]);

  useEffect(() => {
    if (typeof afterFetch === 'function') {
      afterFetch(pagination.totalCount);
    }
  }, [pagination.totalCount]);

  useEffect(() => {
    if (!scrollLoadMore) return;
    if (!scrollLoadMoreRef.current) return;
    if (!pagination.nextPage) return;
    const nextPage = pagination.nextPage;
    const scrollLoadMoreEle = scrollLoadMoreRef.current;
    const loadMore = () => {
      if (isLoading) return;
      const currentPos = window.pageYOffset;
      const loadMorePos = getEleOffset(scrollLoadMoreEle).top - window.innerHeight;
      if (currentPos > loadMorePos) {
        setPage(nextPage);
      }
    };

    document.addEventListener('scroll', loadMore);

    return () => {
      document.removeEventListener('scroll', loadMore);
    };
  }, [scrollLoadMore, pagination.nextPage, isLoading]);

  useEffect(() => {
    if (router.isReady) {
      setPage(qPage ? Number(qPage) : 1);
    }
  }, [record.id, record.type, qPage, JSON.stringify(searchParams), JSON.stringify(queryParams), router]);

  useEffect(() => {
    if (typeof window === 'object') {
      const removePage = () => {
        setPage(0)
      }
      window.document.addEventListener('before_filter', removePage)

      return () => {
        window.document.removeEventListener('before_filter', removePage)
      }
    }
  }, [])

  return show ? (
    <>
      {!hideTitle && (
        <div className="container">
          {renderTitle ? (
            renderTitle()
          ) : (
            <h2 className="tw-font-semibold tw-text-xl md:tw-text-2xl mb-4">{translation('exploreAll')}</h2>
          )}
        </div>
      )}

      <div ref={containerRef} className="container">
        {isEditing ? (
          <OptionEditActions
            options={options}
            pendingOptionChangesById={pendingOptionChangesById}
            onCancel={() => {
              setPendingOptionChangesById({});
              typeof onCancel === 'function' && onCancel();
            }}
            onSave={() => {
              setOptions((prev) =>
                prev.map((option) => ({
                  ...option,
                  ...(pendingOptionChangesById[option.id] || {}),
                }))
              );
              mutate();
              setPendingOptionChangesById({});
              typeof onSave === 'function' && onSave();
            }}
            onGenerated={(subtitles) => {
              setPendingOptionChangesById((prev) => {
                const next = { ...prev };

                Object.entries(subtitles).forEach(([optionId, subtitle]) => {
                  const id = Number(optionId);
                  next[id] = { ...next[id], subtitle };
                });

                return next;
              });
            }}
          />
        ) : null}

        <div className="row">
          {options.map((option) => (
            isEditing ? (
              <OptionCardEdit
                key={`option-edit-${option.id}`}
                option={{...option, ...pendingOptionChangesById[Number(option.id)]}}
                onChange={(value) => {
                  setPendingOptionChangesById((prev) => ({
                    ...prev,
                    [option.id]: { ...prev[option.id], ...value },
                  }));
                }}
              />
            ) : (
              <OptionCard
                key={option.id}
                option={option}
                highlightText={record.type === 'Fts' ? String(record.id) : undefined}
                isGift={isGiftExperience}
                hidePrice={hidePrice}
                useHtmlLink={useHtmlLink}
              />
            )
          ))}
        </div>
        {numSkeletons > 0 && isLoading && options.length === 0 ? (
          <div className="row">
            {range(numSkeletons).map((i) => (
              <OptionCardSkeleton key={i} />
            ))}
          </div>
        ) : null}
      </div>

      <div ref={scrollLoadMoreRef} />

      {!usePagination && isLoading && (options.length > 0 || numSkeletons <= 0) ? (
        <div className="container tw-py-16">
          <Loading className="tw-justify-center" />
        </div>
      ): null}

      {!usePagination && !scrollLoadMore && pagination.nextPage && (
        <div className="tw-mt-0 md:tw-mt-12 tw-text-center">
          <button
            type="button"
            className="btn btn-primary"
            disabled={isLoading}
            onClick={() => {
              setPage((prev) => (prev || 1) + 1);
            }}
          >
            {translation('loadMore')}
          </button>
        </div>
      )}

      {usePagination && pagination.totalPages > 1 ? (
        <Pagination
          className="tw-mt-8 tw-justify-center"
          window={2}
          paginationData={pagination}
          onClick={(page) => {
            router.push({
              pathname: router.pathname,
              query: { ...router.query, page }
            }, undefined, { scroll: false, shallow: true });
            const ele: HTMLDivElement | null = document.querySelector('#after-pagination-scroll-position')
            const scrollToEle = ele || containerRef.current
            if (scrollToEle) {
              scrollTo(scrollToEle, -(headerHeight + pageNavHeight))
            }
          }}
        />
      ) : null}
    </>
  ) : null;
};

export default OtherOptions;
